import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TimeSelect from './TimeSelect';
import { timeCustomersAPI } from '../services/api';
import { CreateTimeLead, TimeCustomerLookup, TimeLead, TimeLeadTag } from '../types';

export const LEAD_STATUSES = [
  { id: 6, label: 'Lead' },
  { id: 9, label: 'Follow-up' },
  { id: 10, label: 'Confirmed' },
  { id: 3, label: 'In Progress' },
  { id: 13, label: 'Internal' },
  { id: 11, label: 'Maintenance' },
  { id: 12, label: 'Lost' },
  { id: 4, label: 'Completed' },
];

const PIPELINE_STATUSES = LEAD_STATUSES.slice(0, 3);

export const toDateInputValue = (value: string) => (value || '').split('T')[0];

export const todayInputValue = () => new Date().toISOString().split('T')[0];

interface LeadFormState {
  title: string;
  customerId: string;
  date: string;
  description: string;
  po: string;
  statusId: string;
  statusReason: string;
  tagIds: number[];
}

const emptyForm: LeadFormState = {
  title: '',
  customerId: '',
  date: todayInputValue(),
  description: '',
  po: '',
  statusId: String(LEAD_STATUSES[0].id),
  statusReason: '',
  tagIds: [],
};

const buildEmptyForm = (): LeadFormState => ({ ...emptyForm, date: todayInputValue() });

const formFromLead = (lead: TimeLead): LeadFormState => ({
  title: lead.title,
  customerId: String(lead.customerId),
  date: toDateInputValue(lead.date),
  description: lead.description ?? '',
  po: lead.po ?? '',
  statusId: String(lead.statusId),
  statusReason: lead.statusReason ?? '',
  tagIds: lead.tags.map(tag => tag.id),
});

interface LeadFormProps {
  lead: TimeLead | null;
  customers: TimeCustomerLookup[];
  tags?: TimeLeadTag[];
  onCustomersChange: (customers: TimeCustomerLookup[]) => void;
  onSubmit: (payload: CreateTimeLead) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveError: string;
}

const LeadForm: React.FC<LeadFormProps> = ({
  lead,
  customers,
  tags = [],
  onCustomersChange,
  onSubmit,
  onCancel,
  saving,
  saveError,
}) => {
  const [form, setForm] = useState<LeadFormState>(() => (lead ? formFromLead(lead) : buildEmptyForm()));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [customerError, setCustomerError] = useState('');
  const [savingCustomer, setSavingCustomer] = useState(false);

  useEffect(() => {
    setForm(lead ? formFromLead(lead) : buildEmptyForm());
    setFormErrors({});
    setAddingCustomer(false);
    setNewCustomerName('');
    setCustomerError('');
  }, [lead]);

  // Include existing assignments even if a tag was archived after it was assigned.
  const tagOptions = [...tags, ...(lead?.tags ?? []).filter(tag => !tags.some(item => item.id === tag.id))]
    .map(tag => ({ value: tag.id, label: tag.name }));

  const resetCustomerCreation = () => {
    setAddingCustomer(false);
    setNewCustomerName('');
    setCustomerError('');
  };

  const startAddingCustomer = () => {
    setAddingCustomer(true);
    setNewCustomerName('');
    setCustomerError('');
  };

  const cancelAddingCustomer = () => {
    resetCustomerCreation();
  };

  // Name-only customer creation so a lead can be saved for a brand new customer.
  const addCustomer = async () => {
    const title = newCustomerName.trim();
    if (!title) {
      setCustomerError('Customer name is required');
      return;
    }

    setSavingCustomer(true);
    setCustomerError('');
    try {
      const created = await timeCustomersAPI.createCustomer(title);
      onCustomersChange(
        [...customers.filter(customer => customer.id !== created.id), created]
          .sort((a, b) => a.title.localeCompare(b.title))
      );
      setForm(current => ({ ...current, customerId: String(created.id) }));
      setFormErrors(current => {
        if (!current.customerId) return current;
        const next = { ...current };
        delete next.customerId;
        return next;
      });
      resetCustomerCreation();
      toast.success('Customer added');
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Customer could not be created.';
      setCustomerError(message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const allowedStatuses = lead ? LEAD_STATUSES : PIPELINE_STATUSES;
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.customerId || Number(form.customerId) < 1) errors.customerId = 'Customer is required';
    if (!form.date) errors.date = 'Date is required';
    if (!form.statusId || !allowedStatuses.some(status => status.id === Number(form.statusId))) {
      errors.statusId = 'Status is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveForm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    const payload: CreateTimeLead = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      date: form.date,
      po: form.po.trim() || undefined,
      customerId: Number(form.customerId),
      statusId: Number(form.statusId),
      statusReason: form.statusReason.trim() || undefined,
      tagIds: form.tagIds,
    };
    await onSubmit(payload);
  };

  return (
    <section className="card card-section" aria-label={lead ? 'Edit lead' : 'New lead'}>
      <div className="card-body">
        <h2 className="card-title">{lead ? `Edit lead: ${lead.title}` : 'New lead'}</h2>
        {saveError && (
          <div role="alert" className="alert alert-danger">
            {saveError}
          </div>
        )}
        <form className="time-form" onSubmit={saveForm} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="lead-title">
              Title <span className="required-mark">*</span>
            </label>
            <input
              id="lead-title"
              className="form-control"
              aria-invalid={formErrors.title ? true : undefined}
              value={form.title}
              onChange={event => setForm(current => ({ ...current, title: event.target.value }))}
              disabled={saving}
            />
            {formErrors.title && <div className="field-error">{formErrors.title}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-customer">
              Customer <span className="required-mark">*</span>
            </label>
            <TimeSelect
              inputId="lead-customer"
              ariaInvalid={formErrors.customerId ? true : undefined}
              options={customers.map(customer => ({ value: String(customer.id), label: customer.title }))}
              value={form.customerId || null}
              onChange={value => setForm(current => ({ ...current, customerId: value ?? '' }))}
              placeholder="Select customer..."
              noOptionsMessage="No customers found"
              isDisabled={saving}
              isClearable
            />
            {formErrors.customerId && <div className="field-error">{formErrors.customerId}</div>}
            <div className="customer-create">
              {!addingCustomer ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={startAddingCustomer}
                  disabled={saving}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" /> New customer
                </button>
              ) : (
                <>
                  <label className="form-label" htmlFor="lead-new-customer">
                    New customer name
                  </label>
                  <input
                    id="lead-new-customer"
                    className="form-control"
                    value={newCustomerName}
                    maxLength={256}
                    aria-invalid={customerError ? true : undefined}
                    onChange={event => {
                      setNewCustomerName(event.target.value);
                      if (customerError) setCustomerError('');
                    }}
                    disabled={savingCustomer}
                  />
                  {customerError && <div className="field-error">{customerError}</div>}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addCustomer}
                      disabled={savingCustomer}
                    >
                      {savingCustomer ? 'Adding...' : 'Add customer'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelAddingCustomer}
                      disabled={savingCustomer}
                      aria-label="Cancel new customer"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-date">
              Date <span className="required-mark">*</span>
            </label>
            <input
              id="lead-date"
              type="date"
              className="form-control"
              aria-invalid={formErrors.date ? true : undefined}
              value={form.date}
              onChange={event => setForm(current => ({ ...current, date: event.target.value }))}
              disabled={saving}
            />
            {formErrors.date && <div className="field-error">{formErrors.date}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-description">
              Description
            </label>
            <textarea
              id="lead-description"
              className="form-control lead-description"
              rows={10}
              value={form.description}
              onChange={event => setForm(current => ({ ...current, description: event.target.value }))}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-po">
              PO
            </label>
            <input
              id="lead-po"
              className="form-control"
              value={form.po}
              onChange={event => setForm(current => ({ ...current, po: event.target.value }))}
              disabled={saving}
              maxLength={20}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-status">
              Status <span className="required-mark">*</span>
            </label>
            <select
              id="lead-status"
              className="form-select"
              aria-invalid={formErrors.statusId ? true : undefined}
              value={form.statusId}
              onChange={event => setForm(current => ({ ...current, statusId: event.target.value }))}
              disabled={saving}
            >
              {(lead ? LEAD_STATUSES : PIPELINE_STATUSES).map(status => (
                <option key={status.id} value={status.id}>
                  {status.label}
                </option>
              ))}
            </select>
            {formErrors.statusId && <div className="field-error">{formErrors.statusId}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-status-reason">
              Status reason
            </label>
            <input
              id="lead-status-reason"
              className="form-control"
              value={form.statusReason}
              onChange={event => setForm(current => ({ ...current, statusReason: event.target.value }))}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lead-tags">Tags</label>
            <TimeSelect<number>
              inputId="lead-tags"
              options={tagOptions}
              value={form.tagIds}
              onChange={tagIds => {
                if (!saving) setForm(current => ({ ...current, tagIds }));
              }}
              placeholder="Select tags..."
              noOptionsMessage="No tags found"
              isMulti
              isClearable
              isDisabled={saving}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : lead ? 'Update lead' : 'Create lead'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default LeadForm;
